/**
 * WebRTC Configuration for the Raccoon App
 * 
 * Includes:
 * - STUN servers for NAT traversal
 * - TURN server structure for fallback (requires credentials in production)
 * - Media constraints for optimal video/audio
 * - ICE configuration options
 */

// Free public STUN servers
const STUN_SERVERS = [
  'stun:stun.l.google.com:19302',
  'stun:stun1.l.google.com:19302',
  'stun:stun2.l.google.com:19302',
  'stun:stun3.l.google.com:19302',
  'stun:stun4.l.google.com:19302',
  'stun:stun.services.mozilla.com',
  'stun:stun.stunprotocol.org:3478'
];

// TURN server configuration - uses short-lived, time-limited credentials
// fetched from the backend (see backend/routes/turn.py) instead of a
// permanent static secret, so nothing long-lived is exposed to the client.
let TURN_SERVERS = [];
let turnCredentialsFetchedAt = 0;
const TURN_CREDENTIALS_REFRESH_MS = 45 * 60 * 1000; // refresh before the 1hr TTL expires

export const fetchTurnCredentials = async () => {
  const now = Date.now();
  if (now - turnCredentialsFetchedAt < TURN_CREDENTIALS_REFRESH_MS && TURN_SERVERS.length > 0) {
    return TURN_SERVERS;
  }
  try {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const response = await fetch(`${backendUrl}/api/turn-credentials`);
    const data = await response.json();
    if (data.uris && data.username && data.credential) {
      TURN_SERVERS = data.uris.map((urls) => ({
        urls,
        username: data.username,
        credential: data.credential
      }));
      turnCredentialsFetchedAt = now;
    }
  } catch (err) {
    console.error('Failed to fetch TURN credentials:', err);
  }
  return TURN_SERVERS;
};

// Full ICE server configuration - OPTIMIZED FOR SPEED
// Call fetchTurnCredentials() before this to ensure TURN_SERVERS is populated.
export const getIceServers = () => ({
  iceServers: [
    // STUN servers for NAT discovery - prioritized by reliability
    ...STUN_SERVERS.slice(0, 3).map(url => ({ urls: url })),

    // TURN servers for relay fallback (short-lived credentials)
    ...TURN_SERVERS
  ],
  iceCandidatePoolSize: 5,  // Reduced for faster gathering
  iceTransportPolicy: 'all'  // Use all available transports
});

// PERFORMANCE: Optimized media constraints for speed
export const VIDEO_CONSTRAINTS = {
  video: {
    width: { ideal: 640, min: 320, max: 1280 },  // Lower default for speed
    height: { ideal: 480, min: 240, max: 720 },
    frameRate: { ideal: 24, min: 15, max: 30 },  // Lower framerate = faster
    facingMode: 'user',
    aspectRatio: { ideal: 4 / 3 }
  },
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    sampleRate: 44100,  // Lower sample rate for speed
    channelCount: 1
  }
};

// Mobile-optimized constraints (lower resolution for better performance)
export const MOBILE_VIDEO_CONSTRAINTS = {
  video: {
    width: { ideal: 640, min: 320, max: 1280 },
    height: { ideal: 480, min: 240, max: 720 },
    frameRate: { ideal: 24, min: 15, max: 30 },
    facingMode: 'user'
  },
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true
  }
};

// Audio-only constraints
export const AUDIO_ONLY_CONSTRAINTS = {
  video: false,
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true
  }
};

// SDP offer options
export const OFFER_OPTIONS = {
  offerToReceiveAudio: true,
  offerToReceiveVideo: true,
  voiceActivityDetection: true
};

// Connection timeout in milliseconds - OPTIMIZED FOR SPEED
export const CONNECTION_TIMEOUT = 15000;  // Faster timeout

// ICE gathering timeout - OPTIMIZED FOR SPEED
export const ICE_GATHERING_TIMEOUT = 5000;  // Faster ICE gathering

// Reconnection attempts
export const MAX_RECONNECTION_ATTEMPTS = 3;
export const RECONNECTION_DELAY = 1000;  // Faster reconnection

// Detect if user is on mobile
export const isMobile = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

// Get appropriate media constraints based on device and optional performance settings
export const getMediaConstraints = (settings) => {
  const base = isMobile() ? MOBILE_VIDEO_CONSTRAINTS : VIDEO_CONSTRAINTS;
  if (!settings) return JSON.parse(JSON.stringify(base));

  const constraints = JSON.parse(JSON.stringify(base));
  if (settings.resolution && constraints.video) {
    constraints.video.width = { ideal: settings.resolution.width || 640, min: 320 };
    constraints.video.height = { ideal: settings.resolution.height || 480, min: 240 };
  }
  if (settings.frameRate && constraints.video) {
    constraints.video.frameRate = { ideal: settings.frameRate, min: 15 };
  }
  return constraints;
};

// Camera filter CSS styles
export const FILTER_STYLES = {
  none: 'none',
  
  // Beauty filters
  beauty: 'brightness(1.08) contrast(1.05) saturate(1.15)',
  smooth: 'brightness(1.1) contrast(0.92) saturate(1.08) blur(0.4px)',
  glow: 'brightness(1.15) contrast(1.02) saturate(1.1)',
  
  // Mask filters (CSS approximation)
  raccoon: 'contrast(1.15) brightness(1.02) saturate(0.9)',
  cat: 'brightness(1.05) contrast(1.1) saturate(1.2)',
  dog: 'brightness(1.08) contrast(1.05) sepia(0.1)',
  
  // Color tone filters
  warm: 'brightness(1.08) sepia(0.25) saturate(1.4) contrast(1.02)',
  cool: 'brightness(1.05) saturate(0.85) hue-rotate(15deg) contrast(1.08)',
  vintage: 'sepia(0.45) contrast(1.15) brightness(0.92) saturate(0.75)',
  bw: 'grayscale(1) contrast(1.2) brightness(1.05)',
  
  // Fun effect filters
  neon: 'brightness(1.2) contrast(1.35) saturate(1.6)',
  sparkle: 'brightness(1.18) contrast(1.08) saturate(1.25)',
  vhs: 'brightness(1.05) contrast(1.25) saturate(1.3) hue-rotate(-5deg)',
  comic: 'contrast(1.5) brightness(1.1) saturate(1.4)',
  dreamy: 'brightness(1.12) contrast(0.9) saturate(1.15) blur(0.5px)'
};

export default getIceServers;
